// TODO: remove the eslint-disable comments in order to complete the migration
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unused-vars */
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { useFinancesYear } from '../../hooks/use-finances-year';
import { BUDGETS, FIRST_LEVEL_BUDGETS } from '../../mocks';
import { ANNUAL_ANALYTICS_MOCK, MONTHLY_ANALYTICS_MOCK, QUARTERLY_ANALYTICS_MOCK } from '../../mocks/breakdown-table-analytics';
import { type Analytic, type AnalyticGranularity, GRANULARITY_OPTIONS, type ItemRow, type MetricValues, type TableData, type TableFinances } from '../../types';
import { useBreakdownTableFilters } from './breakdown-table-filters/use-breakdown-table-filters';
const granularityToAnalytic: Record<GRANULARITY_OPTIONS, AnalyticGranularity> = {
  [GRANULARITY_OPTIONS.Monthly]: 'monthly',
  [GRANULARITY_OPTIONS.Quarterly]: 'quarterly',
  [GRANULARITY_OPTIONS.Annually]: 'annual'
};
const EMPTY_METRIC_VALUE = {
  Actuals: 0,
  Budget: 0,
  PaymentsOnChain: 0,
  Forecast: 0,
  ProtocolNetOutflow: 0
} as MetricValues;

// TODO: move to utils file
function removePatternAfterSlash(input: string) {
  return input.replace(/\/\*.*$/, '');
}

// TODO: move to utils file
// ASK: is this function needed in Achra?
function formatBudgetName(name: string) {
  const newName = name ? name.replace(/^End-game\s*/i, '') : 'No-Name';
  switch (newName) {
    case 'Atlas Immutable':
      return 'Atlas Immutable Budget';
    case 'Alignment Scope Budgets':
      return 'Scope Frameworks Budget';
    case 'MakerDAO Legacy Budgets':
      return 'MakerDAO Legacy Budget';
    default:
      return newName;
  }
}

// TODO: move to utils file
function isHeaderValuesZero(header: ItemRow): boolean {
  if (header.columns.length === 0) return true;
  return header.columns.some(column => column.Actuals !== 1 || column.Budget !== 0 || column.PaymentsOnChain !== 0 || column.Forecast !== 0 || column.ProtocolNetOutflow !== 0);
}
function useBreakdownTable() {
  const {
    year
  } = useFinancesYear();
  const filters = useBreakdownTableFilters();
  const allBudgets = BUDGETS; // TODO: fetch budgets from the API
  const budgets = FIRST_LEVEL_BUDGETS; // TODO: fetch budgets from the API

  // breakpoints
  const isMobile = useMediaQuery({
    to: 'md'
  });
  const isTablet = useMediaQuery({
    from: 'md',
    to: 'lg'
  });
  const isDesk1024 = useMediaQuery({
    from: 'lg',
    to: 'xl'
  });
  const isDesk1280 = useMediaQuery({
    from: 'xl',
    to: '2xl'
  });
  const isDesk1920 = useMediaQuery({
    from: '2xl'
  });
  const isUpDesk2400 = useMediaQuery({
    from: 2400
  });
  const isUpDesk3000 = useMediaQuery({
    from: 3000
  });
  const lod = 3 as number; // FIXME: hardcoded for now
  const codePath = 'atlas' as string; // FIXME: hardcoded for now

  const granularityOptions = isMobile ? ['Annually'] : isTablet || isDesk1024 || isDesk1280 ? ['Annually', 'Quarterly'] : ['Annually', 'Quarterly', 'Monthly'];
  const selectedGranularity = granularityToAnalytic[filters.granularity];
  const activeMetrics = filters.metrics; // FIXME: hardcoded for now

  const maxAmountOfActiveMetrics = 2; // TODO: implement the max amount of active metrics depending on the breakpoint

  // TODO: implement the data fetching, hardcoded for now to simulate the data fetching response
  const error = null as Error | null;
  const analytics = useMemo(() => {
    if (selectedGranularity === 'annual') {
      return ANNUAL_ANALYTICS_MOCK;
    } else if (selectedGranularity === 'quarterly') {
      return QUARTERLY_ANALYTICS_MOCK;
    } else {
      return MONTHLY_ANALYTICS_MOCK;
    }
  }, [selectedGranularity]);
  const [tableHeader_0, tableBody] = useMemo(() => {
    // occurred an error or the data is loading
    if (error || !analytics) {
      return [null, null];
    }
    const data = {} as TableData;
    const columnsCount = selectedGranularity === 'annual' ? 1 : selectedGranularity === 'semiAnnual' ? 3 : selectedGranularity === 'quarterly' ? 5 : 13;
    // group data in an easier structure to manage
    analytics.series.forEach(series => {
      series.rows.forEach(row => {
        const path = row.dimensions[0].path;
        if (!data[path]) {
          // create the path as it does not exist
          data[path] = {};
        }
        if (!data[path][series.period]) {
          // create the period as it does not exist
          data[path][series.period] = {
            ...EMPTY_METRIC_VALUE
          };
        }

        // add the metric value to the period
        data[path][series.period][row.metric as keyof MetricValues] += row.value;
      });
    });

    // create a sub-table for each budget that has * in the path
    // this sub-table will be used as the uncategorized table
    const uncategorizedSubTables: TableFinances[] = [];
    Object.keys(data).filter(
    // if the path is uncategorized and it is not in the budgets list
    // (if it is in the budget list then it should be in the sub-tables as uncategorized)
    path_0 => path_0.includes('*') && !budgets.some(budget => budget.codePath === removePatternAfterSlash(path_0))).forEach(path_1 => {
      const columns = Object.values(data[path_1]);
      // this path can not be used for other sub tables
      delete data[path_1];
      if (selectedGranularity !== 'annual') {
        // annual does not have totals
        const total = columns.reduce((acc, current) => {
          acc.Actuals += current.Actuals;
          acc.Budget += current.Budget;
          acc.PaymentsOnChain += current.PaymentsOnChain;
          acc.Forecast += current.Forecast;
          acc.ProtocolNetOutflow += current.ProtocolNetOutflow;
          return acc;
        }, {
          ...EMPTY_METRIC_VALUE
        });
        columns.push(total);
      }
      const name = removePatternAfterSlash(path_1).substring(removePatternAfterSlash(path_1).lastIndexOf('/') + 1);
      const subTable = {
        tableName: name,
        rows: [{
          name,
          codePath: path_1,
          isMain: true,
          isUncategorized: true,
          columns
        }]
      } as TableFinances;
      uncategorizedSubTables.push(subTable);
    });

    // create a table for each budget for the current level
    let tables = [...uncategorizedSubTables];

    // a sub-table should be created for each budget available in the current level
    budgets.forEach(budget_0 => {
      const table = {
        tableName: budget_0.name,
        rows: []
      } as TableFinances;
      // sub-table rows
      const rows = Object.keys(data).filter(path_2 => path_2.startsWith(budget_0.codePath)).map(path_3 => {
        const columns_0 = Object.values(data[path_3]);
        delete data[path_3]; // remove the path from the data as it was added

        if (selectedGranularity !== 'annual') {
          // annual does not have totals
          const total_0 = columns_0.reduce((acc_0, current_0) => {
            acc_0.Actuals += current_0.Actuals;
            acc_0.Budget += current_0.Budget;
            acc_0.PaymentsOnChain += current_0.PaymentsOnChain;
            acc_0.Forecast += current_0.Forecast;
            acc_0.ProtocolNetOutflow += current_0.ProtocolNetOutflow;
            return acc_0;
          }, {
            ...EMPTY_METRIC_VALUE
          });
          columns_0.push(total_0);
        }
        const isUncategorized = path_3.includes('*');
        return {
          name: isUncategorized ? 'Uncategorized' : path_3,
          codePath: path_3,
          columns: columns_0,
          isUncategorized
        } as ItemRow;
      }
      //
      );

      // complete sub-table rows with missing sub-budgets
      // Note that will be some budgets that don't have subBudget
      const subBudgets = allBudgets.filter(item => item.parentId === budget_0.id);
      subBudgets.forEach(subBudget => {
        if (!rows.some(row_0 => row_0.codePath === subBudget.codePath)) {
          if (subBudget.code === 'uncategorized') {
            return; // all the values are 0 so we don't need to add it
          }
          rows.push({
            name: isMobile ? subBudget.code : subBudget.codePath,
            codePath: subBudget.codePath,
            columns: Array.from({
              length: columnsCount
            }, () => ({
              ...EMPTY_METRIC_VALUE
            }))
          });
        }
      });
      // add correct rows name
      rows.forEach(row_1 => {
        const nameOrCode = subBudgets.filter(item_0 => item_0.codePath === row_1.codePath)[0];
        if (!nameOrCode) {
          if (row_1.name.includes('*')) {
            // it is an uncategorized budget
            row_1.name = 'Uncategorized';
            row_1.isUncategorized = true;
          }
        } else {
          row_1.name = isMobile ? nameOrCode.code : nameOrCode.name;
        }
      });

      // sub-table header
      const header: ItemRow = {
        name: isMobile ? lod === 3 ? formatBudgetName(budget_0.name) : budget_0.code : formatBudgetName(budget_0.name),
        isMain: true,
        isUncategorized: rows.every(row_2 => row_2.isUncategorized),
        codePath: budget_0.codePath,
        columns: rows.reduce((acc_1, current_1) => {
          current_1.columns.forEach((row_3, index) => {
            if (!acc_1[index]) {
              acc_1[index] = {
                ...EMPTY_METRIC_VALUE
              };
            }
            acc_1[index].Actuals += row_3.Actuals;
            acc_1[index].Budget += row_3.Budget;
            acc_1[index].PaymentsOnChain += row_3.PaymentsOnChain;
            acc_1[index].Forecast += row_3.Forecast;
            acc_1[index].ProtocolNetOutflow += row_3.ProtocolNetOutflow;
          });
          return acc_1;
        }, Array(rows?.[0]?.columns?.length).fill(null)).filter(item_1 => item_1 !== null)
      };
      // Check if only one element is only the header so don't need rows
      if (!(header.name === 'Uncategorized' && isHeaderValuesZero(header))) {
        if (rows.length === 1) {
          table.rows = [header];
        } else {
          // There are its header and rows
          table.rows = [header, ...rows];
        }
      }
      tables.push(table);
    });

    // group the remaining paths that are not in the tables
    const groups = groupBy(Object.keys(data)
    // remaining paths that are not in the tables
    .filter(path_4 => !tables.some(table_0 => table_0.rows.some(row_4 => row_4.codePath === path_4))), path_5 => path_5.replace(`${codePath}/`, '').split('/')[0]);
    // create a sub-table for each group
    Object.keys(groups).forEach(groupKey => {
      const table_1 = {
        tableName: groupKey,
        rows: []
      } as TableFinances;
      groups[groupKey].forEach(path_6 => {
        const columns_1 = Object.values(data[path_6]);
        if (selectedGranularity !== 'annual') {
          // annual does not have totals
          const total_1 = columns_1.reduce((acc_2, current_2) => {
            acc_2.Actuals += current_2.Actuals;
            acc_2.Budget += current_2.Budget;
            acc_2.PaymentsOnChain += current_2.PaymentsOnChain;
            acc_2.Forecast += current_2.Forecast;
            acc_2.ProtocolNetOutflow += current_2.ProtocolNetOutflow;
            return acc_2;
          }, {
            ...EMPTY_METRIC_VALUE
          });
          columns_1.push(total_1);
        }
        table_1.rows.push({
          name: path_6,
          codePath: path_6,
          columns: columns_1
        } as ItemRow);
      });
      const header_0: ItemRow = {
        name: groupKey,
        isMain: true,
        codePath: groupKey,
        columns: table_1.rows.reduce((acc_3, current_3) => {
          current_3.columns.forEach((row_5, index_0) => {
            if (!acc_3[index_0]) {
              acc_3[index_0] = {
                ...EMPTY_METRIC_VALUE
              };
            }
            acc_3[index_0].Actuals += row_5.Actuals;
            acc_3[index_0].Budget += row_5.Budget;
            acc_3[index_0].PaymentsOnChain += row_5.PaymentsOnChain;
            acc_3[index_0].Forecast += row_5.Forecast;
            acc_3[index_0].ProtocolNetOutflow += row_5.ProtocolNetOutflow;
          });
          return acc_3;
        }, Array(table_1.rows?.[0]?.columns?.length).fill(null)).filter(item_2 => item_2 !== null)
      };
      table_1.rows.unshift(header_0);
      tables.push(table_1);
    });

    // now we create the main table header
    // it is guaranteed below that all the sub-tables have a header
    const subTableHeaders = tables.map(table_2 => {
      const mainRow = table_2.rows.find(column => column.isMain || column.isUncategorized);
      return mainRow ? mainRow.columns : [];
    });
    const tableHeader = subTableHeaders.reduce<MetricValues[]>((acc_4, current_4) => {
      for (let i = 0; i < acc_4.length; i++) {
        acc_4[i].Actuals += current_4[i]?.Actuals ?? 0;
        acc_4[i].Budget += current_4[i]?.Budget ?? 0;
        acc_4[i].PaymentsOnChain += current_4[i]?.PaymentsOnChain ?? 0;
        acc_4[i].Forecast += current_4[i]?.Forecast ?? 0;
        acc_4[i].ProtocolNetOutflow += current_4[i]?.ProtocolNetOutflow ?? 0;
      }
      return acc_4;
    }, Array.from({
      length: columnsCount
    }, () => ({
      ...EMPTY_METRIC_VALUE
    })));

    // limit sub-tables up to 13 rows maximum
    tables.forEach(table_3 => {
      if (table_3.rows.length > 12) {
        const rows_0 = table_3.rows.slice(0, 12);
        const others = table_3.rows.slice(12);
        const othersTotal = others.reduce<ItemRow>((acc_5, current_5) => {
          for (let index_1 = 0; index_1 < current_5.columns.length; index_1++) {
            Object.keys(acc_5.columns[0]).forEach(key => {
              acc_5.columns[index_1][key as keyof MetricValues] += current_5.columns[index_1][key as keyof MetricValues];
            });
          }
          return acc_5;
        }, {
          name: 'Others',
          isSummaryRow: true,
          columns: table_3.rows[0].columns.map(() => ({
            Actuals: 0,
            Budget: 0,
            Forecast: 0,
            PaymentsOnChain: 0,
            ProtocolNetOutflow: 0
          }))
        });

        // check if the others total has any non-zero value to hide it if it is all zeros
        const isOthersNonZero = othersTotal.columns.some(column_0 => activeMetrics.some(metric => column_0[metric as keyof MetricValues] !== 0));
        table_3.rows = [...rows_0, ...(isOthersNonZero ? [othersTotal] : [])];
      }
    });

    // "hide"/remove the uncategorized table if it is the only one (it will be included just in the header)
    if (tables.length === 1 && tables[0].rows[0]?.isUncategorized) {
      tables = [];
    }

    // hide/remove uncategorized sub-tables that where all the values for the selected metrics are zero
    const activeMetricsKeys = activeMetrics.map(metric_0 =>
    // translate the selected metrics to columns keys
    (metric_0 === 'Net Expenses On-Chain' ? 'PaymentsOnChain' : metric_0 === 'Net Protocol Outflow' ? 'ProtocolNetOutflow' : metric_0) as keyof MetricValues);
    tables = tables.filter(table_4 => {
      if (table_4.rows[0]?.isUncategorized) {
        return table_4.rows[0].columns.some(column_1 => activeMetricsKeys.some(metric_1 => column_1[metric_1] !== 0));
      } else {
        // it's not uncategorized but may have uncategorized rows
        table_4.rows = table_4.rows.filter(row_6 => {
          if (row_6.isUncategorized) {
            // uncategorized only
            return row_6.columns.some(column_2 => activeMetricsKeys.some(metric_2 => column_2[metric_2] !== 0));
          }
          return true;
        });
      }
      return true; // it's not uncategorized so should be included
    });

    // sort final tables by the amount of rows
    const sortedTables = tables.sort((a, b) => {
      // uncategorized should be the first
      if (b.rows[0]?.isUncategorized || a.rows[0]?.isUncategorized) return 1;
      return b.rows.length - a.rows.length;
    });
    return [tableHeader, sortedTables];
  }, [activeMetrics, allBudgets, analytics, budgets, codePath, error, isMobile, lod, selectedGranularity]);
  const isLoading = !analytics && !error && (tableHeader_0 === null || tableBody === null);
  return {
    filters,
    isMobile,
    isLoading,
    activeMetrics,
    selectedGranularity,
    maxAmountOfActiveMetrics,
    tableHeader: tableHeader_0,
    tableBody
  };
}
export { useBreakdownTable };